import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../shared/hooks/useLanguage';
import { useMetaTags } from '../../shared/hooks/useMetaTags';
import { getSiteUrl } from '../../shared/utils/metaTags';
import './Apps.css';

type AppImageIcon = {
  iconSrc: string;
  iconAltJa: string;
  iconAltEn: string;
  icon?: never;
};

type AppSvgIcon = {
  icon: ReactNode;
  iconSrc?: never;
  iconAltJa?: never;
  iconAltEn?: never;
};

type AppEntry = {
  id: string;
  path: string;
  titleJa: string;
  titleEn: string;
  descriptionJa: string;
  descriptionEn: string;
} & (AppImageIcon | AppSvgIcon);

/**
 * ミニアプリ一覧（ハブ）ページ
 * 各ミニアプリへのリンクをカードで表示
 */
function Apps() {
  const lang = useLanguage();
  const siteUrl = getSiteUrl();
  const appsUrl = `${siteUrl}/${lang}/apps`;
  const title = lang === 'ja' ? 'アプリ' : 'Apps';

  useMetaTags({
    title: `${title} | Version0.1`,
    description: title,
    ogp: {
      title: `${title} | Version0.1`,
      description: title,
      url: appsUrl,
      type: 'website',
      siteName: 'Version0.1',
      image: '/og-image.png',
    },
    twitterCard: {
      card: 'summary' as const,
      title: `${title} | Version0.1`,
      description: title,
      image: '/og-image.png',
    },
    canonicalUrl: appsUrl,
  });

  const apps: AppEntry[] = [
    {
      id: 'mynazca',
      path: `/${lang}/apps/mynazca`,
      titleJa: 'MyNazca(α版)',
      titleEn: 'MyNazca (alpha)',
      descriptionJa:
        '画像からGPS走行ルートを作成し、Garmin等のトレーニング記録を地上絵のように書き換えます。',
      descriptionEn:
        'Turn an image into a GPS route and overlay it on your workout track to create ground art.',
      iconSrc: '/mynazca/images/route-tips/osprey_map.png',
      iconAltJa: 'MyNazca 地上絵例（ナスカの地図上のミサゴ）',
      iconAltEn: 'MyNazca ground art example (osprey on a map in Nazca)',
    },
    {
      id: 'cidr-planner',
      path: `/${lang}/apps/cidr-planner`,
      titleJa: 'CIDRプランナー',
      titleEn: 'CIDR Planner',
      descriptionJa: 'CIDRの分割と空き具合を確認できます。',
      descriptionEn: 'Check CIDR splits and free space.',
      iconSrc: '/cidr-planner/images/cidr-planner.png',
      iconAltJa: 'CIDRプランナーのキャンバス例（VPC風のサブネット分割）',
      iconAltEn: 'CIDR Planner canvas example (VPC-style subnet layout)',
    },
    {
      id: 'analytics',
      path: `/${lang}/apps/analytics`,
      titleJa: 'アクセス履歴',
      titleEn: 'Access History',
      descriptionJa: '期間別のアクセス数・国・デバイスなどの詳細をグラフと一覧で確認できます。',
      descriptionEn:
        'View detailed access data by period: counts, countries, devices, and more in charts and tables.',
      iconSrc: '/analytics/images/analytics.png',
      iconAltJa: 'アクセス履歴のグラフ例（ブラウザ別）',
      iconAltEn: 'Access history chart example (by browser)',
    },
  ];

  return (
    <main className="apps-page">
      <div className="apps-page__container">
        <h1 className="apps-page__title">{title}</h1>
        <ul className="apps-page__list">
          {apps.map((app) => (
            <li key={app.id} className="apps-page__item">
              <Link to={app.path} className="apps-page__card">
                {'iconSrc' in app && app.iconSrc ? (
                  <span className="apps-page__card-icon-wrap apps-page__card-icon-wrap--image">
                    <img
                      src={app.iconSrc}
                      alt={lang === 'ja' ? app.iconAltJa : app.iconAltEn}
                      className="apps-page__card-icon-image"
                      width={192}
                      height={192}
                      loading="lazy"
                    />
                  </span>
                ) : (
                  <span className="apps-page__card-icon-wrap">{app.icon}</span>
                )}
                <span className="apps-page__card-body">
                  <span className="apps-page__card-title">
                    {lang === 'ja' ? app.titleJa : app.titleEn}
                  </span>
                  <span className="apps-page__card-desc">
                    {lang === 'ja' ? app.descriptionJa : app.descriptionEn}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </main>
  );
}

export default Apps;
